import { memo, useEffect, useState } from "react";
import { Box, IconButton, Stack, styled, Typography } from "@mui/material";
import { QuoteIcon } from "components/icons";
import { useDispatch, useSelector } from "react-redux";
import { ConversationActions, ConversationSelectors } from "redux-store";
import { getCommonLang, getNSLang } from "utils/lang.utils";
import { LangConstant, SystemConstant } from "const";
import ChatTypography from "../ChatItem/ChatTypography";
import { getPinContent } from "utils/message.utils";
import useDisappearMessage from "../useDisappearMessage";
import { createSelector } from "reselect";
import { refactorMessage } from "../ViewMode.helper";
import { getLatestMessage } from "utils/view.utils";
import { StorageUtil } from "utils";
import { getInteractor } from "services/local.service";
import DecryptMessageView from "../ChatItem/DecryptMessageView";

const updateMsgReduxState = createSelector(
  [
    ConversationSelectors.getSelectedGroupId,
    ConversationSelectors.getThreadingId,
    state => state.conversationRedux.incomingMessages,
  ],
  (selectedGroupId, selectedThreadId, incomingMessages) => {
    const currentIncomingMessage = Object.values(incomingMessages[selectedThreadId || selectedGroupId] || {}) || [];
    const updateMessageSourceIds = currentIncomingMessage
      .filter(
        item =>
          [SystemConstant.SEND_TYPE.deleteMessage, SystemConstant.SEND_TYPE.deleteMessageOnlyMe].includes(
            item.sendType,
          ) ||
          (SystemConstant.EDITABLE_SEND_TYPE.includes(item.sendType) && Boolean(item.parentId)),
      )
      .map(item => item.parentId);
    return { updateMessageSourceIds };
  },
);

const Quote = ({ quoteMessage, endIcon, ...otherProps }) => {
  const dispatch = useDispatch();
  const { disappearListId } = useDisappearMessage({});
  const prefixKey = StorageUtil.getCurrentPrefixKey();

  const { updateMessageSourceIds } = useSelector(updateMsgReduxState);

  const [quoteDetail, setQuoteDetail] = useState({
    id: "",
    sourceId: "",
    senderName: "",
    content: "",
  });
  const [isDecryptFailed, setIsDecryptFailed] = useState(false);

  const handleCloseQuote = () => {
    dispatch(
      ConversationActions.conversationSet({
        quoteMessage: null,
      }),
    );
  };

  const handleSetQuoteDetail = quoteMessage => {
    const newQuoteDetail = {
      id: quoteMessage.id,
      sourceId: quoteMessage.sourceId,
      senderName: quoteMessage.senderName || getCommonLang("TXT_USER_TRIOS"),
    };

    if (
      quoteMessage.sendType === SystemConstant.SEND_TYPE.notFound ||
      quoteMessage.state === SystemConstant.STATE.inactive
    ) {
      newQuoteDetail.content = getCommonLang("TXT_NOT_FOUND_MESSAGE");
      newQuoteDetail.senderName = getCommonLang("TXT_USER_TRIOS");
    } else if (quoteMessage.isDeleteAll) {
      newQuoteDetail.content = getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_MSG_ALL");
    } else if (quoteMessage.isDeleteOnlyMe) {
      newQuoteDetail.content = getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ONLY_ME");
    } else if (
      [...Object.values(SystemConstant.MEDIA_SEND_TYPE), SystemConstant.SEND_TYPE.link].includes(quoteMessage.sendType)
    ) {
      newQuoteDetail.content = `[${getPinContent(quoteMessage)}]`;
    } else {
      newQuoteDetail.content = quoteMessage.content;
    }

    setQuoteDetail(newQuoteDetail);
  };

  const handleUpdateQuoteMessage = async () => {
    const newQuoteMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
      source_id: quoteMessage.sourceId,
      send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
    });

    const refactorQuote = await refactorMessage(newQuoteMessage);
    const latestQuote = await getLatestMessage(prefixKey, refactorQuote);
    handleSetQuoteDetail(latestQuote);
  };

  useEffect(() => {
    handleSetQuoteDetail(quoteMessage);
  }, [quoteMessage]);

  useEffect(() => {
    if (disappearListId.includes(quoteDetail.id)) {
      setQuoteDetail(state => ({ ...state, content: getCommonLang("TXT_NOT_FOUND_MESSAGE") }));
    }
  }, [disappearListId]);

  useEffect(() => {
    if (updateMessageSourceIds.includes(quoteDetail.sourceId)) {
      handleUpdateQuoteMessage();
    }
  }, [updateMessageSourceIds]);

  return (
    <QuoteComponent {...otherProps}>
      <QuoteContentComponent>
        <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5} color="#6E6E6E">
            <QuoteIcon />
            <Typography fontWeight={600}>{quoteDetail.senderName}</Typography>
          </Stack>

          {endIcon && (
            <EndButton edge="end" onClick={handleCloseQuote}>
              {endIcon}
            </EndButton>
          )}
        </Stack>
        {quoteMessage.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover ||
        quoteMessage?.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptFailed ? (
          <DecryptMessageView
            created={quoteMessage.created}
            isMine={true}
            isDecryptFailed={isDecryptFailed}
            setIsDecryptFailed={setIsDecryptFailed}
          />
        ) : (
          <ChatTypography
            messageContent={quoteDetail.content}
            messageId={quoteDetail.id}
            fontSize={13}
            fontWeight={400}
            lineHeight={2.25}
            ml={3}
            className="ellipsis"
            color="#000000"
          />
        )}
      </QuoteContentComponent>
    </QuoteComponent>
  );
};

export default memo(Quote);

const QuoteComponent = styled(Box)({
  width: "100%",
  height: "fit-content",
  backgroundColor: "#F3F3F5",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  position: "relative",
  zIndex: 10,
  padding: 14,
});

const QuoteContentComponent = styled(Stack)(props => ({
  width: "100%",
  color: props.theme.palette.grey[700],
  backgroundColor: props.theme.palette.white,
  borderRadius: 5,
  padding: "6px 12px",
}));

const EndButton = styled(IconButton)({
  padding: 4,
});
